import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "@/components/marketing/legal-page";

export const Route = createFileRoute("/affiliate-terms")({
  head: () => ({
    meta: [
      { title: "Affiliate Partner Terms | CapitalDart" },
      { name: "description", content: "CapitalDart Affiliate Partner Terms" },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Affiliate Partner Terms | CapitalDart" },
      { property: "og:description", content: "CapitalDart Affiliate Partner Terms" },
      { property: "og:url", content: "https://capitaldart.com/affiliate-terms" },
    ],
    links: [{ rel: "canonical", href: "https://capitaldart.com/affiliate-terms" }],
  }),
  component: AffiliateTermsPage,
});

function AffiliateTermsPage() {
  return (
    <LegalPage title="CapitalDart Affiliate Partner Terms" effectiveDate="August 2026">
      <p>Version 1.0 | August 2026</p>

      <p>
        These are the terms and conditions applicable to affiliate partner agreements concluded and performed through the Reditus SaaS partner management platform, located at www.getreditus.com ("Reditus"), between CapitalDart LLC, a Delaware limited liability company with its principal office at 1460 Broadway, New York, NY 10036 ("CapitalDart"), and Reditus users intending to provide CapitalDart with referral services.
      </p>

      <h1>1. Definitions</h1>
      <p>
        The capitalized terms used in these Affiliate Partner Terms have the meaning described in this article. Account: the account through which Partner and CapitalDart access the Reditus platform. Affiliate Partner Terms: the terms and conditions contained herein, including Annex 1, which forms an integral part of the Partner Agreement. Intellectual Property Rights: all intellectual property rights and related rights, including but not limited to copyrights, database rights, domain name rights, trademark rights, brand rights, model rights, neighbouring rights, patent rights and rights to know-how. Lead: a potential customer directed to CapitalDart's services by Partner as a result of Partner's performance of Referral Services, for example via a referral link. Partner Agreement: an agreement between Partner and CapitalDart, of which these Affiliate Partner Terms form an integral part, under which Partner provides the Referral Services. Partner: a legal entity or natural person that uses the Reditus platform to conclude and perform Partner Agreements in exchange for commission. Partner Program: CapitalDart's offer, published on Reditus, for Partners to enroll in a partner program. Party: Partner and CapitalDart together, or either of them. Referral Services: referral services provided by the Partner to CapitalDart in exchange for commission in accordance with the Partner Agreement, as specified in Annex 1.
      </p>

      <h1>2. Conclusion of the Partner Agreement</h1>
      <p>
        <strong>2.1</strong> A Partner Agreement is concluded by Partner's request to enroll in CapitalDart's Partner Program, Partner's acceptance of these Affiliate Partner Terms and Annex 1, and CapitalDart's subsequent acceptance and confirmation of Partner's enrollment, all performed through Reditus. Upon conclusion, Partner is authorized to perform Referral Services in accordance with the Partner Agreement.
      </p>
      <p>
        <strong>2.2</strong> In the event of inconsistencies, Annex 1 prevails over these Affiliate Partner Terms.
      </p>
      <p>
        <strong>2.3</strong> CapitalDart reserves the right to evaluate each application and may reject any application at its sole discretion.
      </p>
      <p>
        <strong>2.4</strong> The right to perform Referral Services is non-exclusive. CapitalDart may engage other partners or affiliates and may perform any promotional activities itself.
      </p>
      <p>
        <strong>2.5</strong> The Partner Agreement does not create a partnership, joint venture, employment, or agency relationship. Neither Party is authorized to enter into agreements on the other Party's behalf.
      </p>

      <h1>3. Obligations of the Partner</h1>
      <p>
        <strong>3.1</strong> On conclusion of the Partner Agreement, Partner gains access to Reditus functionality to facilitate and track Referral Services. Partner will not share its Account with third parties.
      </p>
      <p>
        <strong>3.2</strong> Partner will be provided with tools, such as referral links, to perform the Referral Services.
      </p>
      <p>
        <strong>3.3</strong> Partner will: adhere to all applicable laws and regulations; refrain from performing Referral Services via unlawful channels; refrain from unsolicited electronic communications that would constitute spam; follow reasonable recommendations of CapitalDart regarding the Referral Services; refrain from self-referrals; refrain from buying or bidding on keywords in search or advertising platforms consisting of any part of CapitalDart's trade name or trademarks; refrain from search engine ads on branded terms or domain names, or other ads that would compete with CapitalDart's marketing or cause customer confusion.
      </p>
      <p>
        <strong>3.4</strong> CapitalDart may provide promotional materials. Partner may not alter such materials and will always use the latest versions of materials, information, and pricing provided by CapitalDart.
      </p>
      <p>
        <strong>3.5</strong> Partner will inform the parties it targets in an honest and sincere manner and will not make misleading statements or representations that CapitalDart cannot fulfill. Partner will not present CapitalDart as providing investment advice, brokerage services, or securities placement services; CapitalDart is a software platform.
      </p>
      <p>
        <strong>3.6</strong> CapitalDart may conduct satisfaction research among Leads provided by Partner and may terminate the Partner Agreement with immediate effect if, in its reasonable opinion, further affiliation would harm its reputation.
      </p>
      <p>
        <strong>3.7</strong> Any abuse of the tools provided to perform or track the Referral Services entitles CapitalDart to terminate the Partner Agreement with immediate effect, and rights accrued prior to termination will lapse unless CapitalDart decides otherwise.
      </p>

      <h1>4. Commission and payment</h1>
      <p>
        <strong>4.1</strong> Partner obtains the right to commission on the conditions specified in Annex 1 when a Lead undertakes the qualifying actions specified there.
      </p>
      <p>
        <strong>4.2</strong> Attribution of commission is reported and calculated automatically through the Reditus platform.
      </p>
      <p>
        <strong>4.3</strong> CapitalDart will pay commission through the agreed payment method and currency specified in Annex 1. Partner is responsible for all taxes applicable to the commission. Currency exchange and payment costs are at Partner's expense.
      </p>
      <p>
        <strong>4.4</strong> Commission is paid per the Payment Period specified in Annex 1, provided the Payment Threshold is met.
      </p>
      <p>
        <strong>4.5</strong> If accrued commission is below the Payment Threshold, it carries over to the following Payment Period.
      </p>
      <p>
        <strong>4.6</strong> CapitalDart will provide a specification of commission due at the end of each Payment Period. Partner must notify CapitalDart of errors within one week after the specification is issued, after which it is deemed final.
      </p>

      <h1>5. Term and termination</h1>
      <p>
        <strong>5.1</strong> The Partner Agreement commences on the date of conclusion and has an indefinite duration.
      </p>
      <p>
        <strong>5.2</strong> Either Party may terminate the Partner Agreement without cause, with immediate effect, at any time, upon written notice.
      </p>

      <h1>6. Intellectual property</h1>
      <p>
        <strong>6.1</strong> Nothing in the Partner Agreement transfers any Intellectual Property Rights from one Party to the other.
      </p>
      <p>
        <strong>6.2</strong> CapitalDart and its licensors retain all Intellectual Property Rights in its services, software, and materials.
      </p>
      <p>
        <strong>6.3</strong> Rights in information made available by Partner remain vested in Partner.
      </p>
      <p>
        <strong>6.4</strong> CapitalDart may mention Partner, including Partner's trade name and logos, on its websites and promotional materials.
      </p>
      <p>
        <strong>6.5</strong> Partner is granted the right to use CapitalDart's names and logos solely to provide Referral Services, subject to conditions CapitalDart may stipulate.
      </p>

      <h1>7. Liability</h1>
      <p>
        <strong>7.1</strong> CapitalDart is liable only for direct damages resulting from an attributable failure in performing the Partner Agreement. Liability for indirect damages, including lost profit, lost savings, loss of data, reputational harm, and business interruption, is excluded.
      </p>
      <p>
        <strong>7.2</strong> CapitalDart's liability for direct damages is limited to the commission paid to Partner in the three months prior to the incident.
      </p>
      <p>
        <strong>7.3</strong> These limitations do not apply where damage results from intent or deliberate recklessness of CapitalDart's management.
      </p>
      <p>
        <strong>7.4</strong> Any right to compensation requires written notice to CapitalDart within 30 days of discovery of the damage.
      </p>

      <h1>8. Force majeure</h1>
      <p>
        Neither Party is obliged to perform, or liable for failure to perform, obligations prevented by force majeure, including power or Internet failures, network attacks, malware, civil commotion, natural disaster, terror, war, strikes, and supplier failures.
      </p>

      <h1>9. Confidentiality</h1>
      <p>
        <strong>9.1</strong> The Parties will treat as confidential the contents of the Partner Agreement and information marked or reasonably understood as confidential, and will impose this obligation on their employees and engaged third parties.
      </p>
      <p>
        <strong>9.2</strong> This obligation does not apply to information that is or becomes publicly available without breach, was already in the receiving Party's possession, becomes available from a third party not under a confidentiality obligation, or is independently developed.
      </p>
      <p>
        <strong>9.3</strong> A Party legally compelled to disclose confidential information will give the other Party prompt written notice so it may seek protective measures.
      </p>
      <p>
        <strong>9.4</strong> On termination, each Party will return or destroy the other Party's confidential materials on request.
      </p>

      <h1>10. Amendments</h1>
      <p>
        CapitalDart may amend these Affiliate Partner Terms, including Annex 1, at any time. Amendments take effect two weeks after Partner is notified in writing. If Partner does not accept an amendment, it may terminate the Partner Agreement by the effective date.
      </p>

      <h1>11. Miscellaneous</h1>
      <p>
        <strong>11.1</strong> The Partner Agreement is governed exclusively by the laws of the State of New York, without regard to conflict of law principles.
      </p>
      <p>
        <strong>11.2</strong> Any dispute will be submitted to the state or federal courts located in New York County, New York, unless mandatory law dictates otherwise.
      </p>
      <p>
        <strong>11.3</strong> "Written" or "in writing" includes email and communication via the Reditus platform, provided the sender's identity and content integrity can be adequately established.
      </p>
      <p>
        <strong>11.4</strong> CapitalDart's recorded version of any communication is deemed authentic unless Partner supplies proof to the contrary.
      </p>
      <p>
        <strong>11.5</strong> If any provision is unenforceable, it will be amended to conform to applicable law while preserving its intended meaning.
      </p>
      <p>
        <strong>11.6</strong> CapitalDart may transfer the Partner Agreement to a third party without Partner's consent. Partner may not transfer the Partner Agreement without CapitalDart's prior written permission.
      </p>

      <h1>Annex 1: Referral Services and Variable Term Sheet</h1>
      <p>
        <strong>Referral Services:</strong> promotion of CapitalDart (capitaldart.com) via referral links and approved promotional materials.
      </p>
      <p>
        <strong>Qualifying action:</strong> a completed paid purchase of a CapitalDart plan by a Lead.
      </p>
      <p>
        <strong>Commission:</strong> Commission is calculated as 30% of the amount the Client pays at checkout, excluding taxes and refunded amounts. Promotional rebates or credits issued after purchase do not reduce the commission. Commission is one-time per purchase; CapitalDart plans are one-time purchases, not subscriptions.
      </p>
      <p>
        <strong>Attribution window:</strong> 60 days from the Lead's last click on Partner's referral link.
      </p>
      <p>
        <strong>Refunds:</strong> commission on refunded purchases is reversed.
      </p>
      <p>
        <strong>Payment Period:</strong> monthly.
      </p>
      <p>
        <strong>Payment Threshold:</strong> USD 100.
      </p>
      <p>
        <strong>Currency:</strong> USD.
      </p>
      <p>
        <strong>Payment method:</strong> as supported by the Reditus platform.
      </p>
    </LegalPage>
  );
}
